import React, { useState, useEffect } from "react";
import axios from "axios";
import { useDateRange } from "./dateRangeContext.js";
import "./styles.css";

const MODULE_NAME = "_110_pattern_compare";

function Tooltip({ text }) {
    return (
        <span
            style={{
                marginLeft: 4,
                verticalAlign: "middle",
                cursor: "pointer",
                color: "#888",
                fontSize: 14
            }}
            title={text}
        >
            🛈
        </span>
    );
}

const PatternCompare = () => {
    const { windowDays } = useDateRange();
    const [patterns, setPatterns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");
    const [currentLoadingPattern, setCurrentLoadingPattern] = useState("");

    // Tooltip text from backend tooltips.py
    const TOOLTIP_TEXT = "110 - Pattern Compare: Compares trading patterns across different time periods to identify changes in trading behavior and performance. Uses FIFO for trade matching and realized P/L calculation.";

    // Pattern names to cycle through during loading
    const PATTERN_NAMES = [
        "Concentration Bias",
        "Low Price Stocks",
        "Symbol Attachment",
        "Scalping Behavior",
        "Extended Hours",
        "Near Open",
        "Overtrading",
        "Multi-Day Concentration"
    ];

    const getLookbackDays = (val) => {
        if (!val) return 30;
        if (!isNaN(Number(val))) return Number(val);
        if (val === "YTD") return 365;
        return 30;
    };

    // Cycle through patterns during loading
    useEffect(() => {
        if (!loading && !refreshing) return;

        let patternIndex = 0;
        setCurrentLoadingPattern(PATTERN_NAMES[0]);

        const interval = setInterval(() => {
            patternIndex = (patternIndex + 1) % PATTERN_NAMES.length;
            setCurrentLoadingPattern(PATTERN_NAMES[patternIndex]);
        }, 600); // Change pattern every 600ms

        return () => clearInterval(interval);
    }, [loading, refreshing]);

    const fetchPatterns = async () => {
        setLoading(true);
        setError("");
        try {
            const lookback = getLookbackDays(windowDays);
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/pattern_compare`,
                {
                    params: { lookback_days: lookback, _t: Date.now() },
                    withCredentials: true
                }
            );
            setPatterns(res.data.patterns || []);
        } catch (err) {
            setError("Failed to load metrics.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Trigger backend recalculation
    const handleRefreshAll = async (e) => {
        e.preventDefault();
        setRefreshing(true);
        try {
            const lookback = getLookbackDays(windowDays);
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/pattern-refresh-all`,
                null,
                { params: { lookback_days: lookback }, withCredentials: true }
            );
            // Re-fetch the calculated summary
            await fetchPatterns();
        } catch (err) {
            console.error("Refresh error:", err);
            alert("Global recalculation failed.");
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchPatterns();
    }, [windowDays]);

    const formatPL = (pl) => {
        const val = pl || 0;
        const sign = val >= 0 ? "+" : "-";
        return `${sign}$${Math.abs(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    };

    const renderBars = () => {
        if (patterns.length === 0) return "No data available.";
        const maxAbs = Math.max(...patterns.map(p => Math.abs(p.total_pl || 0)), 1);
        return patterns.map(p => {
            const name = (p.pattern_name || "Unknown").padEnd(28, " ");
            const val = p.total_pl || 0;
            const count = Math.round((Math.abs(val) / maxAbs) * 25);
            const bar = "█".repeat(count);
            return val >= 0
                ? `${name}${" ".repeat(25)}|${bar} ${formatPL(val)}`
                : `${name}${" ".repeat(25 - count)}${bar}| ${formatPL(val)}`;
        }).join("\n");
    };

    return (
        <div style={{ background: "#181A25", padding: "10px", borderRadius: "8px", color: "#ccc", display: "flex", flexDirection: "column", height: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "12px" }}>
                <h4 style={{ margin: 0, color: "#fff" }}>Pattern comparison: (last 30)</h4>
                <Tooltip text={TOOLTIP_TEXT} />
            </div>

            <div style={{ flex: 1, overflow: "auto" }}>
                {loading || refreshing ? (
                    <div style={{ padding: "10px", color: "#4a9eff" }}>
                        <div style={{ marginBottom: "8px" }}>
                            {refreshing ? "⏳ Recalculating" : "📊 Loading"}
                        </div>
                        <div style={{ fontSize: "0.9em", color: "#888" }}>
                            {currentLoadingPattern}
                            <span style={{ animation: "dots 1.5s infinite" }}>.</span>
                        </div>
                        <style>{`
                            @keyframes dots {
                                0%, 20% { content: '.'; }
                                40% { content: '..'; }
                                60%, 100% { content: '...'; }
                            }
                        `}</style>
                    </div>
                ) : error ? (
                    <div style={{ padding: "10px", color: "#e53935" }}>
                        {error}
                    </div>
                ) : (
                    <pre style={{ fontFamily: "monospace", fontSize: "0.85em", margin: 0 }}>
                        {renderBars()}
                    </pre>
                )}
            </div>

            {/* Bottom Refresh Link */}
            <div style={{
                borderTop: "1px solid #333",
                paddingTop: "8px",
                marginTop: "10px",
                textAlign: "right",
                fontSize: "0.75em"
            }}>
                {refreshing ? (
                    <span style={{ color: "#888" }}>
                        ⏳ Recalculating {Math.ceil(Math.random() * 100)}%
                    </span>
                ) : (
                    <a
                        href="#"
                        onClick={handleRefreshAll}
                        style={{ color: "#4a9eff", textDecoration: "none", cursor: "pointer" }}
                    >
                        ↻ Recalculate each pattern
                    </a>
                )}
            </div>
        </div>
    );
};

export default PatternCompare;